import { useMemo, useState } from 'react';
import { Badge, Button, Group, SegmentedControl, Select, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { AircraftList } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatCounter } from '../../lib/format';
import { AircraftFormDrawer } from './AircraftFormDrawer';

export function AircraftListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organisation, can, enumChoices } = useSession();
  const org = organisation!.slug;

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [category, setCategory] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [isFormOpen, setFormOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['aircraft', org, debouncedSearch, category, status],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/', {
          params: {
            query: {
              search: debouncedSearch || undefined,
              category: category || undefined,
              status: status === 'all' ? undefined : status,
              page_size: 200,
              ordering: 'registration',
            },
          },
        }),
      ),
  });

  const columns = useMemo<ColumnDef<AircraftList, unknown>[]>(
    () => [
      {
        accessorKey: 'registration',
        header: t('aircraft.registration'),
        meta: { mono: true, width: 110 },
        cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            <Text fw={600} className="identifier">
              {row.original.registration}
            </Text>
            {row.original.competition_number ? (
              <Badge size="xs" variant="outline" color="gray">
                {row.original.competition_number}
              </Badge>
            ) : null}
          </Group>
        ),
      },
      {
        accessorKey: 'type_label',
        header: t('aircraft.type'),
        cell: ({ row }) => (
          <div>
            <Text fz="sm">{row.original.type_label}</Text>
            <Text fz="xs" c="dimmed">
              {row.original.type_designator}
            </Text>
          </div>
        ),
      },
      {
        accessorKey: 'category',
        header: t('aircraft.category'),
        meta: { width: 120 },
        cell: ({ row }) => (
          <Text fz="sm">{enumLabelFor(enumChoices('aircraft_category'), row.original.category)}</Text>
        ),
      },
      {
        accessorKey: 'aircraft_class',
        header: t('aircraft.class'),
        meta: { width: 90 },
      },
      {
        accessorKey: 'airframe_hours',
        header: t('aircraft.airframeHours'),
        meta: { align: 'right', mono: true, width: 100 },
        cell: ({ row }) => formatCounter(row.original.airframe_hours, 'HOURS'),
      },
      {
        accessorKey: 'landings',
        header: t('aircraft.landings'),
        meta: { align: 'right', mono: true, width: 90 },
        cell: ({ row }) => formatCounter(row.original.landings, 'COUNT'),
      },
      {
        accessorKey: 'maintenance_status',
        header: t('maintenance.due'),
        meta: { width: 130 },
        cell: ({ row }) => <StatusBadge kind="due" value={row.original.maintenance_status} />,
      },
      {
        accessorKey: 'open_alerts',
        header: t('nav.alerts'),
        meta: { align: 'center', width: 80 },
        cell: ({ row }) =>
          row.original.open_alerts ? (
            <Badge color="orange" circle>
              {row.original.open_alerts}
            </Badge>
          ) : (
            <Text c="dimmed">–</Text>
          ),
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        meta: { width: 130 },
        cell: ({ row }) => <StatusBadge kind="aircraft" value={row.original.status} />,
      },
    ],
    [t, enumChoices],
  );

  const rows = data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t('aircraft.fleet')}
        subtitle={`${data?.count ?? 0} × ${t('aircraft.title').toLowerCase()}`}
        actions={
          can('aircraft.change') ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setFormOpen(true)}>
              {t('aircraft.addAircraft')}
            </Button>
          ) : null
        }
      />

      <Group mb="sm" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t('action.search')}
          leftSection={<IconSearch size={15} />}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          w={220}
        />
        <Select
          placeholder={t('aircraft.category')}
          data={enumChoices('aircraft_category').map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          value={category}
          onChange={setCategory}
          clearable
          w={170}
        />
        <SegmentedControl
          value={status}
          onChange={setStatus}
          data={[
            { value: 'all', label: t('common.all') },
            { value: 'AIRWORTHY', label: t('aircraft.AIRWORTHY') },
            { value: 'IN_MAINTENANCE', label: t('aircraft.IN_MAINTENANCE') },
            { value: 'GROUNDED', label: t('aircraft.GROUNDED') },
          ]}
          size="sm"
        />
      </Group>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) =>
          void navigate({
            to: '/$org/aircraft/$registration',
            params: { org, registration: row.registration },
          })
        }
        rowProps={(row) =>
          row.status === 'GROUNDED'
            ? { bg: 'var(--mantine-color-red-light)' }
            : row.status === 'STORED' || row.status === 'SOLD'
              ? { opacity: 0.55 }
              : {}
        }
        emptyTitle={t('aircraft.title')}
        emptyHint={t('common.noResultsHint')}
        minWidth={980}
      />

      <AircraftFormDrawer opened={isFormOpen} onClose={() => setFormOpen(false)} />
    </>
  );
}

function enumLabelFor(choices: { value: string; label: string }[], value: string) {
  return choices.find((choice) => choice.value === value)?.label ?? value;
}
